import { Box, makeStyles, Popover, Theme } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import {
  Children,
  cloneElement,
  FC,
  isValidElement,
  ReactNode,
  useEffect,
  useId,
  useState,
} from 'react';
import { WbButton } from '../WbButton';
import { WbDivider } from '../WbDivider';
import clsx from 'clsx';

export interface WbCardActionsProps {
  children: ReactNode;
  breakpoint: Breakpoint;
  /**
   * applied on desktop container
   */
  className?: string;
}

export type WbCardActionsPinnedPosition = 'start' | 'end' | 'top' | 'bottom';

/**
 * Marker component: children placed here remain always visible,
 * even below the mobile breakpoint, and are not collapsed into the menu.
 *
 * `position` controls where they are rendered in mobile mode:
 * - 'start' / 'end': placed alongside the menu button, on the same row
 * - 'top' / 'bottom': placed on their own row, above or below the menu row
 *
 * In desktop mode it has no effect: the content is rendered
 * in place, as a transparent fragment.
 */
export interface WbCardActionsPinnedProps {
  children: ReactNode;
  position?: WbCardActionsPinnedPosition;
}

export const WbCardActionsPinned: FC<WbCardActionsPinnedProps> = ({
  children,
}) => <>{children}</>;
WbCardActionsPinned.displayName = 'WbCardActionsPinned';

type UseStylesProps = {
  breakpoint: Breakpoint;
};

const useStyles = makeStyles<Theme, UseStylesProps>(theme => ({
  desktop: ({ breakpoint }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: 1,
    gap: theme.spacing(1),

    [`@container (max-width: ${theme.breakpoints.values[breakpoint]}px)`]: {
      display: 'none',
    },
  }),
  mobileWrapper: ({ breakpoint }) => ({
    display: 'none',
    flexDirection: 'column',
    gap: theme.spacing(1),
    [`@container (max-width: ${theme.breakpoints.values[breakpoint]}px)`]: {
      display: 'flex',
      marginLeft: 'auto',
    },
  }),
  mobileRow: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'flex-end',
    gap: theme.spacing(1),
  },
  mobileTop: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  mobileBottom: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  mobileButton: {
    width: '36px',
    minWidth: '36px',
  },
  menuContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    gap: theme.spacing(1),
  },
}));

export const WbCardActions: FC<WbCardActionsProps> = ({
  children,
  breakpoint,
  className,
}) => {
  const classes = useStyles({ breakpoint });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const popoverId = useId();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleResize = () => {
      handleClose();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [open]);

  const transformChildrenForMobile = (childrenNodes: ReactNode): ReactNode => {
    return Children.map(childrenNodes, (child, i) => {
      if (isValidElement(child) && child.type === WbDivider) {
        return cloneElement(child, {
          orientation: 'horizontal',
          key: i,
        } as any);
      }
      return isValidElement(child) ? cloneElement(child, { key: i }) : child;
    });
  };

  // Separate "normal" children (which go into the mobile menu)
  // from pinned children, grouped by position.
  const menuChildren: ReactNode[] = [];
  const pinnedStart: ReactNode[] = [];
  const pinnedEnd: ReactNode[] = [];
  const pinnedTop: ReactNode[] = [];
  const pinnedBottom: ReactNode[] = [];

  const pinnedBucket = (
    position: WbCardActionsPinnedPosition | undefined,
  ): ReactNode[] => {
    switch (position) {
      case 'end':
        return pinnedEnd;
      case 'top':
        return pinnedTop;
      case 'bottom':
        return pinnedBottom;
      case 'start':
      default:
        return pinnedStart;
    }
  };

  const isPinned = (child: ReactNode): boolean =>
    isValidElement(child) &&
    (child.type === WbCardActionsPinned ||
      (child.type as any)?.displayName === 'WbCardActionsPinned');

  Children.forEach(children, (child, i) => {
    if (isValidElement(child) && isPinned(child)) {
      const pinnedProps = child.props as WbCardActionsPinnedProps;
      const target = pinnedBucket(pinnedProps.position);

      Children.forEach(pinnedProps.children, (pinnedChild, j) => {
        target.push(
          isValidElement(pinnedChild)
            ? cloneElement(pinnedChild, { key: `pinned-${i}-${j}` })
            : pinnedChild,
        );
      });
    } else {
      menuChildren.push(child);
    }
  });

  const mobileMenuChildren = transformChildrenForMobile(menuChildren);

  return (
    <>
      <Box className={clsx(classes.desktop, className)}>{children}</Box>
      <Box className={classes.mobileWrapper}>
        {pinnedTop.length > 0 && (
          <Box className={classes.mobileTop}>{pinnedTop}</Box>
        )}
        <Box className={classes.mobileRow}>
          {pinnedStart}
          <WbButton
            aria-label="open actions"
            aria-controls={popoverId}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            icon={<MoreVertIcon />}
            color="default"
            variant="text"
            className={classes.mobileButton}
          />
          <Popover
            id={popoverId}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            getContentAnchorEl={null}
          >
            <Box className={classes.menuContent}>{mobileMenuChildren}</Box>
          </Popover>
          {pinnedEnd}
        </Box>
        {pinnedBottom.length > 0 && (
          <Box className={classes.mobileBottom}>{pinnedBottom}</Box>
        )}
      </Box>
    </>
  );
};
